import axios from "axios";
import { toast } from "sonner";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Soft fair-use warning: show once per session when the server flags we're at
// 70%+ of the daily budget. Not a block, just a heads-up for real power users.
let _warnShown = false;
let refreshing = null;
api.interceptors.response.use(
  (r) => {
    const w = r?.headers?.["x-rate-warning"];
    if (w && !_warnShown) {
      _warnShown = true;
      toast.warning(`Fair-use notice: you're at ${w} daily requests. Our terms allow one person per login.`);
    }
    return r;
  },
  async (error) => {
    const orig = error.config;
    const url = orig?.url || "";
    // Dedicated handling for fair-use breaches so the UI shows the server's message
    // instead of a generic axios error.
    if (error.response?.status === 429) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "You've hit today's fair-use limit — please come back later.");
    } else if (error.response?.status === 423) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Your account is under review — please contact support.");
    }
    const skip = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/logout");
    if (error.response?.status === 401 && orig && !orig._retry && !skip) {
      orig._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return api(orig);
      } catch (_) { /* refresh failed — fall through to original error */ }
    }
    return Promise.reject(error);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (typeof d.msg === "string") return d.msg;
  return String(d);
}

export default api;
